import { gql, TypedDocumentNode } from "@apollo/client";

export const GLOBAL_SEARCH_ENTITY_HITS_PER_PAGE = 10;

export type GlobalSearchPersonNode = {
  id: string;
  pgaId: string | null;
  displayName: string;
  profilePhoto: string | null;
  primarySection: { id: string | null; name: string | null } | null;
  primaryFacility?: { id: string | null; name: string | null; addresses: { state: string | null }[] } | null;
  employments?: { facility: { id: string | null } | null }[] | null;
};

export type GlobalSearchPeopleCareerSearchResponse = {
  fuzzySearchPeople: { nodes: Pick<GlobalSearchPersonNode, "id" | "pgaId" | "primaryFacility" | "employments">[] };
};

export type GlobalSearchPeopleCareerSearchVariables = {
  search: string;
  first: number;
  recentlyViewedIds: string[];
};

export type GlobalSearchFacilityNode = {
  id: string;
  name: string;
  legacyId: string | null;
  section: { id: string; name: string | null } | null;
  mainAddress: { city: string | null; state: string | null } | null;
};

export type GlobalSearchPeopleSearchResponse = {
  fuzzySearchPeople: { nodes: GlobalSearchPersonNode[] };
};

export type GlobalSearchPeopleSearchVariables = {
  search: string;
  first: number;
  recentlyViewedIds: string[];
};

export type GlobalSearchFacilitiesSearchResponse = {
  fuzzySearchFacilities: { nodes: GlobalSearchFacilityNode[] };
};

export type GlobalSearchFacilitiesSearchVariables = {
  search: string;
  first: number;
  recentlyViewedIds: string[];
};

export type GlobalSearchJobNode = {
  id: string;
  name: string;
  stage: string | null;
  status: string | null;
  owner: { name: string | null } | null;
  facility: {
    id: string | null;
    name: string | null;
    section: { id: string | null; name: string | null } | null;
  } | null;
};

export type GlobalSearchActivityNode = {
  id: string;
  title: string;
  internalDescription: string | null;
  allowParticipationStarting: string | null;
  allowParticipationThrough: string | null;
  activityType: { id: string | null; title: string | null } | null;
  managingSection: { id: string | null; name: string | null } | null;
  event: {
    slug: string | null;
    title: string | null;
    startsAt: string | null;
    endsAt: string | null;
  } | null;
};

export type GlobalSearchJobsSearchResponse = {
  fuzzySearchJobs: { nodes: GlobalSearchJobNode[] };
};

export type GlobalSearchJobsSearchVariables = {
  searchTerm: string;
  first: number;
  recentlyViewedIds: string[];
};

export type GlobalSearchActivitiesSearchResponse = {
  fuzzySearchActivities: { nodes: GlobalSearchActivityNode[] };
};

export type GlobalSearchActivitiesSearchVariables = {
  search: string;
  first: number;
  recentlyViewedIds: string[];
};

const PERSON_SELECTION = `
        id
        pgaId
        displayName
        profilePhoto
        primarySection {
          id
          name
        }
`;

const FACILITY_SELECTION = `
        id
        name
        legacyId
        section {
          id
          name
        }
        mainAddress {
          city
          state
        }
`;

export const GLOBAL_SEARCH_PEOPLE_SEARCH_QUERY: TypedDocumentNode<
  GlobalSearchPeopleSearchResponse,
  GlobalSearchPeopleSearchVariables
> = gql`
  query GlobalSearchPeopleSearch($search: String!, $first: Int!, $recentlyViewedIds: [ID!]) {
    fuzzySearchPeople(input: { search: $search }, first: $first, recentlyViewedIds: $recentlyViewedIds) {
      nodes {
        ${PERSON_SELECTION}
      }
    }
  }
`;

export const GLOBAL_SEARCH_PEOPLE_CAREER_SEARCH_QUERY: TypedDocumentNode<
  GlobalSearchPeopleCareerSearchResponse,
  GlobalSearchPeopleCareerSearchVariables
> = gql`
  query GlobalSearchPeopleCareerSearch($search: String!, $first: Int!, $recentlyViewedIds: [ID!]) {
    fuzzySearchPeople(input: { search: $search }, first: $first, recentlyViewedIds: $recentlyViewedIds) {
      nodes {
        id
        pgaId
        primaryFacility {
          id
          name
          addresses {
            state
          }
        }
        employments {
          facility {
            id
          }
        }
      }
    }
  }
`;

export const GLOBAL_SEARCH_FACILITIES_SEARCH_QUERY: TypedDocumentNode<
  GlobalSearchFacilitiesSearchResponse,
  GlobalSearchFacilitiesSearchVariables
> = gql`
  query GlobalSearchFacilitiesSearch($search: String!, $first: Int!, $recentlyViewedIds: [ID!]) {
    fuzzySearchFacilities(
      input: { search: $search, excludeArchived: true }
      first: $first
      recentlyViewedIds: $recentlyViewedIds
    ) {
      nodes {
        ${FACILITY_SELECTION}
      }
    }
  }
`;

const JOB_SELECTION = `
        id
        name
        stage
        status
        owner {
          name
        }
        facility {
          id
          name
          section {
            id
            name
          }
        }
`;

const ACTIVITY_SELECTION = `
        id
        title
        internalDescription
        allowParticipationStarting
        allowParticipationThrough
        activityType {
          id
          title
        }
        managingSection {
          id
          name
        }
        event {
          slug
          title
          startsAt
          endsAt
        }
`;

export const GLOBAL_SEARCH_JOBS_SEARCH_QUERY: TypedDocumentNode<
  GlobalSearchJobsSearchResponse,
  GlobalSearchJobsSearchVariables
> = gql`
  query GlobalSearchJobsSearch($searchTerm: String!, $first: Int!, $recentlyViewedIds: [ID!]) {
    fuzzySearchJobs(input: { searchTerm: $searchTerm }, first: $first, recentlyViewedIds: $recentlyViewedIds) {
      nodes {
        ${JOB_SELECTION}
      }
    }
  }
`;

export const GLOBAL_SEARCH_ACTIVITIES_SEARCH_QUERY: TypedDocumentNode<
  GlobalSearchActivitiesSearchResponse,
  GlobalSearchActivitiesSearchVariables
> = gql`
  query GlobalSearchActivitiesSearch($search: String!, $first: Int!, $recentlyViewedIds: [ID!]) {
    fuzzySearchActivities(filters: { search: $search }, first: $first, recentlyViewedIds: $recentlyViewedIds) {
      nodes {
        ${ACTIVITY_SELECTION}
      }
    }
  }
`;
